import {
  Injectable,
  Renderer2,
  RendererFactory2,
  SecurityContext,
} from '@angular/core';
import * as L from 'leaflet';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LatLngBounds, Map } from 'leaflet';

interface GestureHandlingMapOptions extends L.MapOptions {
  gestureHandling?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LeafletMapboxService {
  private readonly accessToken =
    'pk.eyJ1IjoiY2hlZ25lciIsImEiOiJjbDN5NWZpcmUwZTkyM2NuNXQ3NGJwOGExIn0.R-Cl0ThFwUS6xI1S61rEtQ';

  private renderer: Renderer2;
  private tileLayerCache: { [key: string]: L.TileLayer } = {};

  private tiles: { [key: string]: string } = {
    //dark: 'assets/vector-layers/dark.json',
    light: 'assets/vector-layers/light.json',
    traffic: 'assets/vector-layers/traffic.json',
    streets: 'assets/vector-layers/streets.json',
    satellite: 'assets/vector-layers/satellite.json',
  };

  constructor(
    private rendererFactory: RendererFactory2,
    private sanitizer: DomSanitizer
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  initializeMap(mapContainer: HTMLElement): Map {
    const mapOptions: GestureHandlingMapOptions = {
      doubleClickZoom: true,
      boxZoom: true,
      touchZoom: true,
      scrollWheelZoom: false,
      gestureHandling: true,
      zoomControl: false, // Disable the default zoom control
      zoomSnap: 0.25,
    };

    return new Map(mapContainer, mapOptions);
  }

  /**
   * Creates and returns a Mapbox GL layer of the specified type.
   *
   * @param {string} type - The type of Mapbox GL layer to create.
   * @return {L.Layer} The created Mapbox GL layer.
   */
  createMapboxGLLayer(type: string): L.Layer {
    if (!this.tileLayerCache[type]) {
      this.initMapboxGLLayer(type);
    }

    return this.tileLayerCache[type];
  }

  /**
   * Initializes a Mapbox GL layer of the specified type and caches it.
   *
   * @param {string} type - The type of the layer to initialize. This corresponds to a key in the `tiles` object.
   * @return {void} This function does not return any value.
   */
  initMapboxGLLayer(type: string): void {
    this.tileLayerCache[type] = (L as any).mapboxGL({
      accessToken: this.accessToken,
      style: this.tiles[type],
      preserveDrawingBuffer: true,
      attribution:
        '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
    });
  }

  /**
   * Remove all layers from the map except the specified one.
   * @param map Leaflet map instance.
   * @param excludeLayer Layer to keep on the map.
   */
  clearLayers(map: L.Map, excludeLayer?: L.Layer): void {
    map.eachLayer(layer => {
      if (excludeLayer && layer === excludeLayer) return;
      map.removeLayer(layer);
    });
  }

  getMapBoxToken(): string {
    return this.accessToken;
  }

  createMarker(
    latlng: L.LatLngExpression,
    svgCode: SafeHtml | undefined,
    classColor: string,
    iconColor: string,
    size = 1
  ): L.Marker {
    const marker = L.marker(latlng);
    let html = '';
    if (svgCode) {
      const svg = this.sanitizer.sanitize(SecurityContext.HTML, svgCode) || '';
      html = `<div class="" style="transform: scale(${size});"><div class="poi-icon" style="background-color: ${classColor}; fill:${iconColor};">${svg}</div></div>`;
    }

    const divIcon = L.divIcon({
      className: '',
      html: html,
      iconSize: [40 * size, 40 * size],
      iconAnchor: [20 * size, 40 * size],
    });

    // Update the marker's icon
    marker.setIcon(divIcon);

    return marker;
  }

  getBoundsFromObject(bounds: any): LatLngBounds {
    const sw = bounds._southWest;
    const ne = bounds._northEast;
    return L.latLngBounds(L.latLng(sw.lat, sw.lng), L.latLng(ne.lat, ne.lng));
  }
}
